/**
 * StrideCard — STRIDE category distribution as a pie chart rendered
 * through the shared 2.0 Chart primitives (components/ui/Chart over
 * Recharts; replaced the old canvas-based react-google-charts pie,
 * which could not read CSS tokens). Slice colors stay on the canonical
 * `getStrideColor` so STRIDE reads the same here as everywhere else
 * (color follows the entity). The tokenized legend (label · count · %)
 * is kept — it carries counts the generic ChartLegendContent doesn't.
 *
 * Pure presentation; consumes `data.stride`.
 */
import React from 'react';
import { Pie, PieChart } from 'recharts';
import { getStrideColor } from '../../../../utils/severityStyle';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../../components/ui/Chart';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import type { AggregatedOverviewData, StrideKey } from '../../aggregate/types';
import styles from './StrideCard.module.css';

interface StrideCardProps {
  data: AggregatedOverviewData;
}

// Chart primitives are JSX modules; cast for use from TSX (mirrors the
// ButtonAny / LoadingStateAny convention across the codebase).
const ChartContainerAny = ChartContainer as unknown as React.ComponentType<any>;
const ChartTooltipAny = ChartTooltip as unknown as React.ComponentType<any>;
const ChartTooltipContentAny = ChartTooltipContent as unknown as React.ComponentType<any>;

// lowercase StrideKey (slice data) → uppercase enum (getStrideColor /
// canonical STRIDE identity) + display label.
const ROWS: { key: StrideKey; enum: string; label: string }[] = [
  { key: 'spoofing',               enum: 'SPOOFING',               label: 'Spoofing' },
  { key: 'tampering',              enum: 'TAMPERING',              label: 'Tampering' },
  { key: 'repudiation',            enum: 'REPUDIATION',            label: 'Repudiation' },
  { key: 'information_disclosure', enum: 'INFORMATION_DISCLOSURE', label: 'Info Disclosure' },
  { key: 'denial_of_service',      enum: 'DENIAL_OF_SERVICE',      label: 'Denial of Service' },
  { key: 'elevation_of_privilege', enum: 'ELEVATION_OF_PRIVILEGE', label: 'Elev. of Privilege' },
];

export default function StrideCard({ data }: StrideCardProps) {
  const stride = data.stride;
  const total = ROWS.reduce((s, r) => s + stride[r.key], 0);

  if (total === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <TypographyH2 className={styles.cardTitle}>STRIDE Threat Distribution</TypographyH2>
        </div>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No threats identified yet</EmptyTitle>
            <EmptyDescription>Populates after Step 4.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const slices = ROWS.map((r) => ({
    ...r,
    count: stride[r.key],
    pct: Math.round((stride[r.key] / total) * 100),
    color: getStrideColor(r.enum),
  })).filter((r) => r.count > 0);

  const chartData = slices.map((s) => ({
    name: s.label,
    value: s.count,
    fill: s.color,
  }));

  const chartConfig = Object.fromEntries(
    slices.map((s) => [s.key, { label: s.label, color: s.color }]),
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>STRIDE Threat Distribution</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>
          {total} threat{total === 1 ? '' : 's'}
        </TypographyMuted>
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrap}>
          <ChartContainerAny config={chartConfig} className={styles.pieChart}>
            <PieChart>
              <ChartTooltipAny content={<ChartTooltipContentAny hideLabel />} />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                label={({ percent }: { percent?: number }) =>
                  `${Math.round((percent ?? 0) * 100)}%`
                }
              />
            </PieChart>
          </ChartContainerAny>
        </div>

        <ul className={styles.legend}>
          {slices.map((s) => (
            <li key={s.key} className={styles.legendRow}>
              <span className={styles.swatch} style={{ background: s.color }} aria-hidden="true" />
              <TypographySmall className={styles.legendLabel}>{s.label}</TypographySmall>
              <TypographySmall className={styles.legendValue}>
                {s.count} · {s.pct}%
              </TypographySmall>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
